import { Alert, Anchor, Button, Group, Image, Modal, ScrollArea, Stack, Text } from '@mantine/core'
import { IconArrowBackUp } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useAppTranslation } from 'hooks/useTranslation'
import { DeskQueueRow, queryDeskRawItem } from 'supabase/desk'
import { deskImagePaths, publicImageUrl } from './deskImages'

/**
 * Allowlist, not a blocklist (the ApplicationEdit `isHttpsUrl` rule): the
 * source URL is page data, and the click happens inside an admin session.
 */
const isHttpsUrl = (value: string | null | undefined): value is string =>
  typeof value === 'string' && value.startsWith('https://')

export type DiscardedRawItemModalProps = {
  /** The Discard pile row of a raw item with no candidate; null when closed. */
  row: DeskQueueRow | null
  onClose: () => void
  onRestore: (row: DeskQueueRow) => void
  restoring: boolean
}

/**
 * "Not an event" (UI-SPEC §E): a post the editor discarded whole, read-only,
 * so Dominique can read it before restoring it (DESK-06, D-12). The post text
 * and the editor's reason are untrusted and render as plain `Text` children
 * with `pre-wrap`, never as HTML (T-33-53).
 */
export const DiscardedRawItemModal = ({ row, onClose, onRestore, restoring }: DiscardedRawItemModalProps) => {
  const { t } = useAppTranslation()
  const rawItemId = row?.raw_item_id ?? null

  // The same key the candidate screen uses, so a decision's ['desk']
  // invalidation refreshes it too.
  const { isLoading, isError, data: rawItem } = useQuery({
    queryKey: ['desk', 'raw', rawItemId],
    queryFn: () => queryDeskRawItem(rawItemId as number),
    enabled: rawItemId !== null
  })

  const content = rawItem?.content?.trim() ? rawItem.content : null
  const reason = rawItem?.status_reason?.trim() || row?.discard_reason?.trim() || null
  const images = rawItem ? deskImagePaths(rawItem) : []

  const body = () => {
    if (isError) return <Alert color="red">{t('desk.error.load')}</Alert>
    if (isLoading || !rawItem) return <Text c="dimmed">{t('common.loading')}</Text>
    return (
      <Stack gap={16}>
        <ScrollArea.Autosize mah={360}>
          {content ? (
            <Text data-testid="desk-raw-text" size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {content}
            </Text>
          ) : (
            <Text size="sm" c="dimmed">
              {t('desk.candidate.no_text')}
            </Text>
          )}
        </ScrollArea.Autosize>

        {images.length > 0 && (
          <Group gap={8}>
            {images.map((path, index) => (
              <Image
                key={path}
                src={publicImageUrl(path)}
                alt={`${t('desk.fields.image')} ${index + 1}`}
                w={96}
                h={96}
                fit="cover"
                radius="sm"
              />
            ))}
          </Group>
        )}

        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
          {reason ? t('desk.discard.editor_reason', { reason }) : t('desk.discard.no_reason')}
        </Text>

        {isHttpsUrl(rawItem.source_url) && (
          <Anchor size="sm" href={rawItem.source_url} target="_blank" rel="noopener noreferrer">
            {t('desk.candidate.open_original')}
          </Anchor>
        )}
      </Stack>
    )
  }

  return (
    <Modal opened={row !== null} onClose={onClose} size="lg" title={t('desk.discard.modal_title')}>
      <Stack gap={24}>
        {body()}
        <Group justify="flex-end" gap={8}>
          <Button variant="default" onClick={onClose}>
            {t('desk.discard.close')}
          </Button>
          <Button
            variant="light"
            leftSection={<IconArrowBackUp size={16} />}
            loading={restoring}
            disabled={restoring || row === null}
            onClick={() => row && onRestore(row)}
          >
            {t('desk.restore.button')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
